/**
 * Renders any enum value as a badge, deriving both label and tone from the registry.
 * A screen writes `<EnumBadge enum="policyStatus" value={policy.status} />` and never
 * repeats a status literal or picks a colour by hand.
 */

import { Badge, StatusDot, type BadgeTone } from '@/components/ui/badge';
import { useI18n } from '@/lib/i18n';
import { namespaceOf, toneOf, valuesOf, type EnumName } from '@/lib/registry/enums';
import { cn } from '@/lib/utils';

interface EnumBadgeProps {
  enum: EnumName;
  value: string | null | undefined;
  size?: 'default' | 'sm';
  className?: string;
  /** Renders a dot + text instead of a filled badge — for dense table rows. */
  variant?: 'badge' | 'dot';
  /**
   * Overrides the registry tone for the rare value whose urgency depends on more than the
   * value itself — `expired` past the non-renewal horizon is the case this exists for. Callers
   * pass the result of a named rule (`policyStatusTone`), never a colour chosen by hand, so
   * the registry stays the only place a tone is spelled out.
   */
  tone?: BadgeTone;
}

export function EnumBadge({
  enum: name,
  value,
  size,
  className,
  variant = 'badge',
  tone: toneOverride,
}: EnumBadgeProps) {
  const { te } = useI18n();
  if (!value) return <span className="text-muted-foreground">—</span>;

  const label = te(namespaceOf(name), value);
  const tone = toneOverride ?? toneOf(name, value);

  if (variant === 'dot') {
    return (
      <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap', className)}>
        <StatusDot tone={tone} />
        {label}
      </span>
    );
  }

  return (
    <Badge tone={tone} size={size} className={className}>
      {label}
    </Badge>
  );
}

/** Plain label, for places where a badge would be too much chrome. */
export function EnumLabel({ enum: name, value }: { enum: EnumName; value: string | null | undefined }) {
  const { te } = useI18n();
  return <>{value ? te(namespaceOf(name), value) : '—'}</>;
}

export interface EnumOption {
  value: string;
  label: string;
  tone: BadgeTone;
}

/** Option list for selects and filters — generated, never hand-listed. */
export function useEnumOptions(name: EnumName): EnumOption[] {
  const { te } = useI18n();
  const namespace = namespaceOf(name);
  return valuesOf(name).map((value) => ({
    value,
    label: te(namespace, value),
    tone: toneOf(name, value),
  }));
}
